import React, {ReactElement, useMemo} from 'react';
import {Typography, Logo} from '@customink/pigment-react';
import {concat} from 'lodash';
import {useAppSelector, useDomPurify} from '../../hooks';
import {useGetStoreDetailsQuery} from '../../api/storesApi';
import services from '../../api/remoteServices';
import {useSupportInfo} from '../../hooks/useSupportInfo';
import './index.scss';

interface Props {
  storeSlug: string;
  preview: boolean;
}

const FOOTER = {className: 'store-layout-footer'};
Object.freeze(FOOTER);

function formatUrlForDisplay(companyUrl: string) {
  if (!companyUrl) return '';

  const url = new URL(companyUrl);
  return concat(url.hostname, url.pathname);
}

const StoreFooter: React.FC<Props> = ({
  storeSlug,
  preview
}: Props): ReactElement => {
  useGetStoreDetailsQuery(storeSlug);
  const storeDetails = useAppSelector(state => state.stores.storeDetails);
  const localImages = useAppSelector(state => state.stores.localImages);
  const logoImageUrl = localImages?.logo || storeDetails?.logoImageUrl;
  const supportInfo = useSupportInfo('store-layout-footer');
  const {sanitizer} = useDomPurify();

  const handleHomepage = () => {
    window.location.href = services.homePage;
  };

  const businessInfo = useMemo(() => {
    if (storeDetails) {
      return (
        <div
          className={`stores-Footer-businessInfo-content${
            preview ? ' preview' : ''
          }`}>
          {logoImageUrl ? (
            <img
              id="logo"
              className="storeLogo"
              src={logoImageUrl}
              alt={storeSlug}
            />
          ) : (
            <Typography
              className="storeName"
              variant="subHeading"
              weight="semibold"
              gutterBottom>
              {storeDetails?.name}
            </Typography>
          )}

          <div
            className="storeDescription"
            dangerouslySetInnerHTML={{
              __html: sanitizer(storeDetails.description)
            }}
          />
          <a href={storeDetails.companyUrl} target="_blank" rel="noreferrer">
            {formatUrlForDisplay(storeDetails.companyUrl)}
          </a>
        </div>
      );
    }
    return <></>;
  }, [storeDetails, localImages]);

  const contactInfo = () => {
    return (
      <div
        className={`stores-Footer-contactInfo-content${
          preview ? ' preview' : ''
        }`}>
        <Typography variant="subHeading">Powered by:</Typography>
        <Logo
          className="logoImg"
          variant="horizontal"
          onClick={handleHomepage}
        />
        <Typography variant="bodyLong2">
          <strong>
            Check the status of your order <span className="accent">here</span>
          </strong>
        </Typography>
        <Typography variant="bodyLong2" gutterBottom>
          <strong>Need help with your order? Contact us:</strong>
        </Typography>

        <Typography className="contactUs" variant="bodyLong2" gutterBottom>
          <span className="accent">{supportInfo?.phoneNumber}</span> |{' '}
          <a className="accent" href={`mailto:${supportInfo?.email}`}>
            {supportInfo?.email}
          </a>
        </Typography>
        <Typography className="hours" variant="bodyLong2">
          {supportInfo?.hours}
        </Typography>
      </div>
    );
  };

  return (
    <div className={FOOTER.className}>
      <div className="stores-Footer">
        <div className="stores-Footer-container">
          <div className="stores-Footer-businessInfo">{businessInfo}</div>

          <div className="stores-Footer-contactInfo">{contactInfo()}</div>
        </div>

        <div className="stores-Footer-banner">
          <Typography
            className="stores-Footer-banner-text"
            variant="captionLarge">
            &copy; Custom Ink, LLC. All rights reserved.
          </Typography>
          <Typography
            className="stores-Footer-banner-text"
            variant="captionLarge">
            <a
              href={`${services.homePage}/about/privacy`}
              target="_blank"
              rel="noreferrer">
              Privacy Policy
            </a>{' '}
            &nbsp;| &nbsp;{' '}
            <a
              href={`${services.homePage}/about/user_agreement`}
              target="_blank"
              rel="noreferrer">
              User Agreement
            </a>
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default StoreFooter;
